export {};

type ApiConfig = {
  baseUrl: string;
};

const API_PORT = 8000;

// --------------------------------------------------
// Low-level fetch helper (cookies only)
// --------------------------------------------------
async function apiFetch(url: string, init: RequestInit = {}): Promise<Response> {
  return fetch(url, {
    ...init,
    credentials: "include", // REQUIRED for ama_session cookie
  });
}

// --------------------------------------------------
// Base URL resolution
// --------------------------------------------------
let base: string;
const { protocol, host, hostname, port } = window.location;

/**
 * Local dev:
 *   frontend: http://localhost:3000
 *   backend:  http://localhost:8000
 *
 * Dev / prod:
 *   same origin (proxy / ingress)
 */
if (process.env.NODE_ENV === "development" && port !== "") {
  base = `${protocol}//${hostname}:${API_PORT}`;
} else {
  base = `${protocol}//${host}`;
}

const defaultConfig: ApiConfig = {
  baseUrl: `${base}/api/v1`,
};

console.log("API BASE URL:", defaultConfig.baseUrl);

// --------------------------------------------------
// Types
// --------------------------------------------------
export type Scenario = {
  scenario_id: number;
  scenario_name: string;
  description: string | null;
};

export type Student = {
  student_id: number;
  student_code?: string;
  student_name?: string;
  age?: number;
  gender?: string;
  nationality?: string;
  personality?: string;
  flexibility?: string;
  social_environment?: string;
  family_support?: string;
  economic_status?: string;
  academic_ability?: string;
  [key: string]: any;
};

export type ScenarioBundle = {
  scenario: Scenario;
  student: Student;
  initial_prompt: string;
};

export type SessionTiming = {
  duration_total_sec: number;
  duration_coach_sec: number;
  duration_student_sec: number;
  duration_coach_pct: number;
  duration_student_pct: number;
};

export type Session = {
  session_id: string;
  user_id: string;
  scenario_id: number;
  student_id: number;
  status: "completed" | "paused";
  start_ts: string;
  end_ts: string;
  timing?: SessionTiming;
};

export type Message = {
  message_id: string;
  role: "coach" | "student";
  content: string;
  time_start: string;
  time_end: string;
};

export type ConversationPostBody = {
  session: Session;
  messages: Message[];
};

export type FeedbackPostBody = {
  session: Session;
  scenario: Scenario;
  student: Student;
  messages: Message[];
};

export type FeedbackMalGap = {
  gap: string;
  rationale: string;
  primary: boolean;
};

export type FeedbackMal = {
  planning: {
    gaps: FeedbackMalGap[];
  };
  learning: {
    focus_skill: string;
    practice_suggestion: string;
  };
  assessing: {
    success_criteria: string;
  };
  adjusting: {
    next_session_commitment: string;
  };
};

export type FeedbackPayload = {
  feedback_summary: string;
  strengths: string[];
  opportunities_for_growth: string[];
  behavior_scores: Array<{
    domain_code: string;
    competency_code: string;
    behavior_name: string;
    score: number;
    evidence: string[];
    suggestion: string;
  }>;
  mal?: FeedbackMal;
};

export type GetFeedbackResponse = {
  session_id: string;
  feedback: FeedbackPayload;
};

// --------------------------------------------------
// Helpers
// --------------------------------------------------
function parseWrapped<T>(raw: any): T {
  if (raw && typeof raw === "object" && "data" in raw) {
    return raw.data as T;
  }
  return raw as T;
}

async function assertOk(resp: Response, method: string, url: string): Promise<void> {
  if (resp.ok) return;
  const body = await resp.text().catch(() => "");
  throw new Error(`${method} ${url} failed: ${resp.status} ${resp.statusText} - ${body}`);
}

// --------------------------------------------------
// API calls (ALL cookie-based)
// --------------------------------------------------
export async function getScenarios(
  config: ApiConfig = defaultConfig
): Promise<Scenario[]> {
  const url = `${config.baseUrl}/scenarios/`;

  const resp = await apiFetch(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  await assertOk(resp, "GET", url);

  const raw = await resp.json();
  const data = parseWrapped<any>(raw);

  if (Array.isArray(data)) return data;
  throw new Error("Unexpected scenarios response");
}

export async function getScenarioById(
  scenarioId: number,
  config: ApiConfig = defaultConfig
): Promise<ScenarioBundle> {
  const url = `${config.baseUrl}/scenarios/${scenarioId}`;

  const resp = await apiFetch(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  await assertOk(resp, "GET", url);

  const raw = await resp.json();
  return parseWrapped<ScenarioBundle>(raw);
}

export async function postConversation(
  body: ConversationPostBody,
  config: ApiConfig = defaultConfig
): Promise<any> {
  const url = `${config.baseUrl}/conversations/`;

  const resp = await apiFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  await assertOk(resp, "POST", url);

  const text = await resp.text();
  return text ? JSON.parse(text) : {};
}

export async function getFeedback(
  sessionId: string,
  config: ApiConfig = defaultConfig
): Promise<FeedbackPayload> {
  const url = `${config.baseUrl}/feedback/${sessionId}`;

  const resp = await apiFetch(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  await assertOk(resp, "GET", url);

  const raw = await resp.json();
  const data = parseWrapped<GetFeedbackResponse>(raw);
  return data.feedback;
}

export async function getFeedbackWithConversation(
  body: FeedbackPostBody,
  config: ApiConfig = defaultConfig
): Promise<GetFeedbackResponse> {
  const url = `${config.baseUrl}/feedback/generate`;

  const resp = await apiFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  await assertOk(resp, "POST", url);

  const raw = await resp.json();
  return parseWrapped<GetFeedbackResponse>(raw);
}

export async function warmupFeedbackAgent(
  config: ApiConfig = defaultConfig
): Promise<void> {
  const url = `${config.baseUrl}/feedback/warmup`;

  const resp = await apiFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });

  await assertOk(resp, "POST", url);
}
